import { CreditCard, PiggyBank, PieChart, HandCoins, LifeBuoy, LayoutGrid, LineChart, Landmark } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import {
  ShieldCheck, TrendingUp, Share2, Wallet, ArrowRight, ChevronLeft, ChevronRight,
  Trophy, Star, Layers, Sparkles, BadgeCheck, CheckCircle2,
} from "lucide-react";
import api, { inr, getPublicSettings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InstallAppButton } from "@/components/InstallAppButton";

const CATEGORY_ICON = { "Credit Card": "💳" };

const CAT_ICON = {
  "Demat Account": LineChart, "Savings Account": PiggyBank, "Mutual Fund": PieChart,
  "EMI Card": Wallet, "Credit Card": CreditCard, "Personal Loan": HandCoins,
  "KYC/CIBIL Based Loan": ShieldCheck, "Insurance": LifeBuoy, "Other": LayoutGrid,
};

function Hero() {
  const nav = useNavigate();
  const { t: tr } = useLanguage();
  return (
    <section className="relative overflow-hidden bg-brand-gradient text-white">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #2563EB 0, transparent 42%), radial-gradient(circle at 80% 20%, #0D9488 0, transparent 35%)" }} />
      <div className="relative max-w-[1280px] mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide mb-6">
            <Sparkles className="w-3.5 h-3.5 text-teal-300" /> {tr("hero_badge")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            {tr("hero_title_1")} <span className="text-teal-300">{tr("hero_title_2")}</span>
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-lg">
            {tr("hero_subtitle")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => nav("/register")} data-testid="hero-register-btn"
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-bold rounded-full px-7 h-12 text-base shadow-lg shadow-[#2563EB]/30">
              {tr("hero_cta_start")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button onClick={() => nav("/products")} variant="outline" data-testid="hero-products-btn"
              className="bg-white/5 border-white/25 text-white hover:bg-white/15 font-semibold rounded-full px-7 h-12 text-base">
              {tr("hero_cta_browse")}
            </Button>
            <InstallAppButton variant="outline" />
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-sm">
            {[[tr("hero_feat_1"), Layers], [tr("hero_feat_2"), ShieldCheck], [tr("hero_feat_3"), Share2]].map(([t, I]) => (
              <div key={t} className="flex items-center gap-2 text-white/70"><I className="w-4 h-4 text-teal-300" />{t}</div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="hidden lg:block">
          <div className="relative rounded-3xl bg-white/5 border border-white/15 p-6 backdrop-blur-xl shadow-[0_0_90px_-20px_rgba(37,99,235,0.55)]">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">Your Earnings Wallet</span>
              <Wallet className="w-5 h-5 text-teal-300" />
            </div>
            <div className="font-mono-num text-4xl font-extrabold">₹48,250</div>
            <div className="text-teal-300 text-sm font-semibold mt-1 flex items-center gap-1"><TrendingUp className="w-4 h-4" /> +₹5,400 this week</div>
            <div className="grid grid-cols-3 gap-3 mt-6">
              {[["Direct", "₹28,600"], ["Upstream", "₹14,250"], ["Pending", "₹5,400"]].map(([l, v]) => (
                <div key={l} className="bg-white/5 rounded-xl p-3">
                  <div className="text-[10px] text-white/50 uppercase font-semibold">{l}</div>
                  <div className="font-mono-num font-bold mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CampaignCarousel({ campaigns }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true });
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();
  const scrollTo = useCallback((i) => embla && embla.scrollTo(i), [embla]);
  useEffect(() => {
    if (!embla) return;
    const onSel = () => setIdx(embla.selectedScrollSnap());
    embla.on("select", onSel);
    const t = setInterval(() => embla.scrollNext(), 5000);
    return () => clearInterval(t);
  }, [embla]);
  if (!campaigns.length) return null;
  return (
    <section className="max-w-[1280px] mx-auto px-4 sm:px-6 -mt-10 relative z-10">
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-2xl shadow-slate-900/10" ref={emblaRef}>
        <div className="flex">
          {campaigns.map((c) => (
            <div key={c.id} className="relative flex-[0_0_100%] min-h-[360px] md:min-h-[440px]">
              {c.desktop_creative && <img src={c.desktop_creative} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B223D] via-[#0B223D]/85 to-[#153654]/60" />
              <div className="relative h-full flex flex-col justify-center p-8 md:p-14 max-w-2xl text-white">
                <div className="inline-flex w-fit items-center gap-2 bg-[#2563EB]/90 px-3 py-1 rounded-full text-xs font-bold mb-2">
                  <BadgeCheck className="w-3.5 h-3.5" /> EARN REWARDS PER CONFIRMED LEAD
                </div>
                <p className="text-[10px] text-white/60 mb-4 -mt-1" data-testid="hero-tnc-note">*T&amp;C Apply. Payout per confirmed lead; not guaranteed income.</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold leading-tight">{c.name.replace(" Campaign", "")}</h2>
                <p className="mt-3 text-white/80 text-base md:text-lg max-w-lg">{c.description}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button onClick={() => nav(c.campaign_code ? `/apply/${c.campaign_code}` : `/products/${c.product_id}`)} data-testid={`campaign-apply-${c.id}`}
                    className="bg-white text-[#0B223D] hover:bg-slate-100 font-bold rounded-full px-6 h-11">
                    Apply Now
                  </Button>
                  <Button onClick={() => nav("/refer")} variant="outline" data-testid={`campaign-refer-${c.id}`}
                    className="bg-transparent border-white/40 text-white hover:bg-white/10 font-semibold rounded-full px-6 h-11">
                    <Share2 className="w-4 h-4 mr-1.5" /> Get Referral Link
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => embla && embla.scrollPrev()} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg" data-testid="carousel-prev">
          <ChevronLeft className="w-5 h-5 text-[#0B223D]" />
        </button>
        <button onClick={() => embla && embla.scrollNext()} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg" data-testid="carousel-next">
          <ChevronRight className="w-5 h-5 text-[#0B223D]" />
        </button>
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
          {campaigns.map((_, i) => (
            <button key={i} onClick={() => scrollTo(i)} className={`h-2 rounded-full transition-all ${i === idx ? "w-7 bg-[#2563EB]" : "w-2 bg-white/50"}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Section({ eyebrow, title, children, id }) {
  return (
    <section id={id} className="max-w-[1280px] mx-auto px-6 py-16">
      <div className="mb-10">
        {eyebrow && <p className="text-xs font-bold uppercase tracking-widest text-emerald-brand mb-2">{eyebrow}</p>}
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0B223D] tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function Home() {  const { t: tr } = useLanguage();
  useSEO({ title: "Refer, Apply & Earn", path: "/", description: "Credit Kendra — India's direct Refer & Earn fintech marketplace. Apply for demat accounts, loans, credit cards, mutual funds & insurance, share referral links and earn transparent multi-level rewards.", keywords: "Credit Kendra, refer and earn India, demat referral, loan referral, credit card referral, earn money online, fintech marketplace India" });
  const [campaigns, setCampaigns] = useState([]);
  const [allCamps, setAllCamps] = useState([]);
  const [cats, setCats] = useState([]);
  const [top, setTop] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/campaigns/public?featured=true").then((r) => {
      if (r.data.length) setCampaigns(r.data);
      else api.get("/campaigns/public").then((x) => setCampaigns(x.data)).catch(() => {});
    }).catch(() => {});
    api.get("/campaigns/public").then((r) => setAllCamps(r.data)).catch(() => {});
    getPublicSettings().then((d) => { if (d.product_categories?.length) setCats(d.product_categories); }).catch(() => {});
    api.get("/top-performers?limit=5").then((r) => setTop(r.data)).catch(() => {});
    api.get("/feedback/public").then((r) => setFeedback(r.data)).catch(() => {});
  }, []);

  const steps = [
    ["Register free", "Get a permanent Credit Kendra User ID in seconds."],
    ["Pick a campaign", "Choose from demat, loans, cards & more."],
    ["Set your payout", "Decide the split you offer downstream."],
    ["Earn & withdraw", "Rewards hit your wallet on conversion."],
  ];

  return (
    <div>
      <Hero />
      <CampaignCarousel campaigns={campaigns} />

      {/* Browse by category — tap par us category ke products khulte hain */}
      {cats.length > 0 && (
        <Section eyebrow="Categories" title="Sell & Earn — Choose a category">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {cats.map((name) => {
              const Icon = CAT_ICON[name] || Landmark;
              const cc = allCamps.filter((c) => (c.category || "") === name);
              const pct = cc.find((c) => c.payout_type === "percent" && c.payout_percent);
              const mx = Math.max(0, ...cc.map((c) => Number(c.payout_pool) || 0), ...cc.flatMap((c) => (c.payout_slabs || []).map((s) => Number(s.payout) || 0)));
              return (
                <button key={name} onClick={() => nav(`/products?category=${encodeURIComponent(name)}`)} data-testid={`home-cat-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden text-left hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <div className="p-4 pb-2 flex items-start justify-between gap-2">
                    <p className="font-bold text-sm text-[#0B223D] leading-tight">{name}</p>
                    <Icon className="w-7 h-7 shrink-0 text-emerald-brand" />
                  </div>
                  <div className="px-4 pb-2.5">
                    <p className="text-[10px] text-slate-400">Earn upto</p>
                    <p className="font-mono-num font-extrabold text-base text-[#0B223D]">{pct ? `${pct.payout_percent}%` : mx ? inr(mx) : "—"}</p>
                  </div>
                  <div className="text-center text-[11px] font-bold text-white bg-[#0B223D] py-1.5">View offers</div>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {allCamps.length > 0 && (
      <Section eyebrow="Marketplace" title="Featured Financial Products">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allCamps.slice(0, 6).map((c) => {
            const img = c.desktop_creative || c.mobile_creative || "";
            return (
            <Link to={`/apply/${c.campaign_code}`} key={c.id} data-testid={`product-card-${c.campaign_code}`}
              className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 hover:border-[#2563EB]/40 transition-all">
              <div className="h-44 flex items-center justify-center bg-gradient-to-br from-slate-50 to-[#EAF2FF] p-4 relative">
                {img ? (
                  <img src={img} alt={c.name} loading="lazy" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <span className="font-display font-bold text-[#0B223D] text-center px-4">{c.name}</span>
                )}
                <span className="absolute top-3 right-3 bg-[#0D9488]/10 text-[#0D9488] border border-[#0D9488]/20 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                  {c.payout_type === "percent" && c.payout_percent ? `Earn ${c.payout_percent}%` : `Earn up to ${inr(Number(c.payout_pool || c.max_payout || 0))}`}
                </span>
              </div>
              <div className="p-5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-brand">{c.category || "Campaign"}</span>
                <h3 className="font-display font-bold text-lg text-[#0B223D] mt-1">{c.name}</h3>
                {c.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{c.description}</p>}
                <div className="mt-4 flex items-center justify-end">
                  <span className="text-emerald-brand font-bold flex items-center gap-1 text-sm">Apply <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      </Section>
      )}

      {/* Refer & Earn */}
      <section className="bg-[#0B223D] text-white">
        <div className="max-w-[1280px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-3">Refer &amp; Earn</p>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold">One pool. Fair splits. No hidden margin.</h2>
            <p className="mt-4 text-white/75 text-lg">Company configures the campaign payout — say <b className="font-mono-num text-teal-300">₹1,000</b>. You and your network divide it transparently.</p>
            <div className="mt-8 space-y-3">
              {[["You refer a friend, keep ₹800", "₹800 → friend's conversion"], ["You earn the upstream difference", "₹200 → You"], ["Every rupee is traceable in the ledger", "Total always = ₹1,000"]].map(([a, b]) => (
                <div key={a} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-teal-400 shrink-0 mt-0.5" />
                  <div><p className="font-semibold">{a}</p><p className="text-sm text-white/60 font-mono-num">{b}</p></div>
                </div>
              ))}
            </div>
            <Button onClick={() => nav("/tools")} className="mt-8 bg-[#2563EB] hover:bg-[#1d4ed8] font-bold rounded-full px-7 h-12 shadow-lg shadow-[#2563EB]/30">
              Try SIP / EMI Calculators <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
          <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-3xl p-8 border border-white/10">
            <h4 className="font-display font-bold text-xl mb-5">{tr("ex_title")}</h4>
            {[[tr("ex_l1"), 1000, 200], [tr("ex_l2"), 800, 200], [tr("ex_l3"), 600, 600]].map(([n, alloc, reward]) => (
              <div key={n} className="flex items-center justify-between py-3.5 border-b border-white/10 last:border-0">
                <div><p className="font-semibold">{n}</p><p className="text-xs text-white/50">{tr("ex_alloc")} {inr(alloc)}</p></div>
                <span className="font-mono-num font-bold text-teal-300 text-lg">+{inr(reward)}</span>
              </div>
            ))}
            <div className="flex justify-between mt-4 pt-3 border-t border-white/20 text-sm"><span className="text-white/60">{tr("ex_total")}</span><span className="font-mono-num font-bold">₹1,000</span></div>
          </div>
        </div>
      </section>

      <Section eyebrow="Simple" title="How it works">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {steps.map(([t, d], i) => (
            <div key={t} className="relative bg-white rounded-2xl border border-slate-200 p-6">
              <div className="w-10 h-10 rounded-xl bg-[#0B223D] text-white font-display font-bold flex items-center justify-center mb-4">{i + 1}</div>
              <h3 className="font-display font-bold text-lg text-[#0B223D]">{t}</h3>
              <p className="text-sm text-slate-500 mt-2">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Top performers */}
      <Section eyebrow="Leaderboard" title="🏆 Top Performers">
        {top.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">
            <Trophy className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            Rankings appear here as real partners earn approved rewards.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {top.map((t) => (
              <div key={t.rank} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display font-extrabold text-lg ${t.rank === 1 ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"}`}>#{t.rank}</div>
                <div className="flex-1">
                  <p className="font-display font-bold text-[#0B223D]">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.referrals} referrals · {t.badge}</p>
                </div>
                <span className="font-mono-num font-bold text-emerald-brand">{inr(t.earnings)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Testimonials */}
      <Section eyebrow="Community" title="What our users say">
        {feedback.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">
            Approved, genuine reviews from verified users will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {feedback.slice(0, 6).map((f, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex gap-0.5 mb-3">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className={`w-4 h-4 ${j < f.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div>
                <p className="text-slate-600 text-sm leading-relaxed">"{f.comment}"</p>
                <div className="mt-4 flex items-center gap-2">
                  <span className="font-semibold text-[#0B223D] text-sm">{f.display_name}</span>
                  {f.verified_user && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-brand bg-teal-50 px-2 py-0.5 rounded-full"><BadgeCheck className="w-3 h-3" /> Verified</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* FAQ */}
      <Section eyebrow="Help" title="Frequently asked questions" id="faq">
        <div className="grid md:grid-cols-2 gap-5">
          {[
            ["Is registration free?", "Yes. You get a permanent Credit Kendra User ID and can start referring immediately."],
            ["How is the payout split decided?", "The company sets the campaign pool. You choose how much of your allocation to keep vs. offer downstream — it's locked when you create a link."],
            ["When can I withdraw?", "After your KYC is verified and your reward is approved, you can withdraw to bank or UPI."],
            ["Do old rewards change if a campaign rate changes?", "Never. Historical conversions keep the exact rules locked at the time of the referral link."],
          ].map(([q, a]) => (
            <div key={q} className="bg-white rounded-2xl border border-slate-200 p-6">
              <h4 className="font-display font-bold text-[#0B223D]">{q}</h4>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="max-w-[1280px] mx-auto px-6 pb-20">
        <div className="bg-brand-gradient rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold">Ready to turn your network into rewards?</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">Join Credit Kendra and start earning transparent, multi-level referral rewards today.</p>
          <Button onClick={() => nav("/register")} className="mt-7 bg-white text-[#0B223D] hover:bg-slate-100 font-bold rounded-full px-8 h-12 text-base">
            Create free account <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
